import { DEFAULT_LAB_ENGINE_OVERHEAD_SECONDS, DEFAULT_PUMP_CONFIG, pumpDurationSeconds } from "utils/pumpFormat";

// Radar lab: words, numbers and plan arithmetic for the /radar-lab page.
// The server owns every rule (limits, tiers, budgets, checks); these helpers
// only phrase and preview what it sends. "Lab run" throughout: the field pages
// already use "session" for live mode.

export const SELECTION_KEY = "radar_lab_selection";
export const ACTIVE_STATES = [
  "arming",
  "baseline",
  "stepping",
  "holding",
  "final",
  "stopping",
  "restoring",
];
export const TERMINAL_STATES = ["completed", "stopped", "failed"];

const RUN_STATES = {
  arming: { label: "Getting the node ready", tone: "is-waiting" },
  baseline: { label: "Recording the starting level", tone: "is-running" },
  stepping: { label: "Stepping", tone: "is-running" },
  holding: { label: "Holding: no new pump step", tone: "is-waiting" },
  final: { label: "Recording the final level", tone: "is-running" },
  stopping: { label: "Stopping", tone: "is-waiting" },
  restoring: { label: "Restoring the node's settings", tone: "is-waiting" },
  completed: { label: "Completed", tone: "is-running" },
  stopped: { label: "Stopped", tone: "is-off" },
  failed: { label: "Stopped automatically", tone: "is-bad" },
};
export const runStateInfo = (state) =>
  RUN_STATES[state] || { label: state || "Unknown", tone: "is-off" };
export const isActiveRun = (run) =>
  !!run && (run.active === true || ACTIVE_STATES.includes(run.state));

const STEP_STATES = {
  pending: "Waiting",
  requested: "In progress",
  dwelling: "Recording the settled level",
  done: "Done",
  skipped: "Skipped",
  failed: "Failed",
  cancelled: "Cancelled",
};
export const stepStateText = (state) => STEP_STATES[state] || state || "";

export const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const trim = (n, digits) =>
  Number(n.toFixed(digits)).toLocaleString("en-SG", {
    maximumFractionDigits: digits,
  });

export const formatCm = (value, digits = 1) => {
  const n = finiteNumber(value);
  return n === null ? "—" : `${trim(n, digits)} cm`;
};
export const formatSignedCm = (value, digits = 1) => {
  const n = finiteNumber(value);
  if (n === null) return "—";
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${trim(Math.abs(n), digits)} cm`;
};
export const minutesText = (seconds) => {
  const s = finiteNumber(seconds);
  if (s === null) return "—";
  const m = s / 60;
  return `${trim(m, m < 10 ? 1 : 0)} min`;
};

export const signedCm = (step) =>
  (step?.direction === "drain" ? -1 : 1) * (finiteNumber(step?.cm) ?? 0);
export const stepText = (step) =>
  `${step?.direction === "drain" ? "Drain" : "Fill"} ${trim(finiteNumber(step?.cm) ?? 0, 1)} cm`;

// "+1 cm × 2, −1 cm × 2": the moves in order, repeats folded.
export function planText(steps) {
  const parts = [];
  (steps || []).forEach((step) => {
    const move = formatSignedCm(signedCm(step));
    const last = parts[parts.length - 1];
    if (last && last.move === move) last.n += 1;
    else parts.push({ move, n: 1 });
  });
  return parts.map((p) => (p.n > 1 ? `${p.move} × ${p.n}` : p.move)).join(", ");
}

// The cm the level moves per commanded cm ({fill, drain}: the status's
// calibration); 1 when the server sends none.
const factorOf = (factors, direction) => finiteNumber(factors?.[direction === "drain" ? "drain" : "fill"]) ?? 1;
const moved = (step, factors) => signedCm(step) * factorOf(factors, step?.direction);

// The tank level after each step, from a starting level (through the pump's
// calibration when given); a target step's is its target, as the server plans.
export function plannedLevels(start, steps, factors = null) {
  const level = finiteNumber(start);
  if (level === null) return [];
  let current = level;
  return (steps || []).map((step) => {
    const target = finiteNumber(step?.target_cm);
    current = target !== null ? target : current + moved(step, factors);
    return Math.round(current * 100) / 100;
  });
}

// Whether a plan's levels stay inside the tank bounds, with the margin the
// server keeps (at least one step), and clear of its overflow guards: no fill
// that could pass the tank's ceiling (at the pump's factor plus its spread)
// and no level more than max_drain_cm below the start. A preview only: the
// server decides.
export function levelsFit(start, steps, limits, factors = null) {
  const level = finiteNumber(start);
  const low = finiteNumber(limits?.tank_min_cm);
  const high = finiteNumber(limits?.tank_max_cm);
  if (level === null) return { ok: false, detail: "No starting level." };
  if (low === null || high === null) return { ok: true, detail: "" };
  const biggest = Math.round(Math.max(0, ...(steps || []).map((s) => Math.abs(moved(s, factors)))) * 100) / 100;
  const planned = plannedLevels(level, steps, factors);
  // The start only has to lie within the bounds (the water is already
  // there); the levels the plan takes it to beyond the start keep the margin.
  if (level < low - 1e-9 || level > high + 1e-9)
    return { ok: false, detail: `The tank is at ${formatCm(level)}, outside its bounds ${formatCm(low)} to ${formatCm(high)}.` };
  const lo = Math.min(level, ...planned);
  const hi = Math.max(level, ...planned);
  if (lo < level && lo < low + biggest - 1e-9)
    return {
      ok: false,
      detail: `Goes down to ${formatCm(lo)}; the floor is ${formatCm(low)} plus a margin of ${formatCm(biggest, 2)}.`,
    };
  if (hi > level && hi > high - biggest + 1e-9)
    return {
      ok: false,
      detail: `Goes up to ${formatCm(hi)}; the top is ${formatCm(high)} less a margin of ${formatCm(biggest, 2)}.`,
    };
  const ceiling = finiteNumber(limits?.tank_ceiling_cm);
  if (ceiling !== null) {
    const worst = factorOf(factors, "fill") + (finiteNumber(limits?.ceiling_factor_spread) ?? 0);
    let before = level;
    for (let i = 0; i < (steps || []).length; i += 1) {
      const step = steps[i];
      const reach = before + (finiteNumber(step?.cm) ?? 0) * worst;
      if (step?.direction !== "drain" && reach > ceiling + 1e-9)
        return {
          ok: false,
          detail: `A fill of ${formatCm(step.cm)} from ${formatCm(before, 2)} could reach ${formatCm(reach, 2)}, over the tank ceiling of ${formatCm(ceiling, 2)}.`,
        };
      before = planned[i];
    }
  }
  const deepest = finiteNumber(limits?.max_drain_cm);
  if (deepest !== null && lo < level - deepest - 1e-9)
    return {
      ok: false,
      detail: `Goes down to ${formatCm(lo)}, more than ${formatCm(deepest)} below the start.`,
    };
  return { ok: true, detail: "" };
}

// The same steps with fill and drain swapped: the plan from the other side of
// its start level.
export const mirroredSteps = (steps) =>
  (steps || []).map((s) => ({ ...s, direction: s?.direction === "drain" ? "fill" : "drain" }));

// A step preset's plan from a ruler reading, as the server orients it when it
// starts (auto): the preset as written when it fits, else its mirror when
// that fits (and the preset has one), else as written. The status's steps are
// already oriented from the derived level (`mirrored` when that took the
// mirror). -> {steps, fit, mirrored}
export function rulerPlan(preset, start, limits, factors = null) {
  const shown = preset?.steps || [];
  const written = preset?.mirrored ? mirroredSteps(shown) : shown;
  const fit = levelsFit(start, written, limits, factors);
  const hasMirror = preset?.mirrored === true || Object.keys(preset?.orientations || {}).length > 1;
  if (!fit.ok && hasMirror) {
    const mirror = mirroredSteps(written);
    const mirrorFit = levelsFit(start, mirror, limits, factors);
    if (mirrorFit.ok) return { steps: mirror, fit: mirrorFit, mirrored: true };
  }
  return { steps: written, fit, mirrored: false };
}

// ---- Target presets: steps aimed at levels on the ruler ----

// A target preset (mode "target") or lab run: its steps aim at levels on the
// ruler, each commanded when it is due from the level the step before it
// settled at (the server's lab.Runner.aim()).
export const isTargetPlan = (item) => item?.mode === "target";
export const bandOf = (item) =>
  Array.isArray(item?.band) && item.band.length === 2 && item.band.every((v) => finiteNumber(v) !== null)
    ? [finiteNumber(item.band[0]), finiteNumber(item.band[1])]
    : null;
// "35–37.5 cm"
export const bandText = (band) => {
  const b = bandOf({ band });
  return b ? `${formatCm(b[0], 2).replace(/ cm$/, "")}–${formatCm(b[1], 2)}` : "—";
};
// The levels a target preset's steps aim at, in a few words for its button.
export function targetPlanText(preset) {
  const steps = preset?.steps || [];
  const targets = steps.map((s) => finiteNumber(s?.target_cm)).filter((v) => v !== null);
  const parts = finiteNumber(preset?.parts) ?? 1;
  const band = bandOf(preset);
  if (!targets.length)
    return band ? `Through ${bandText(band)}, worked out from the tank's level` : "Worked out from the tank's level";
  const n = `${steps.length} step${steps.length === 1 ? "" : "s"}`;
  const down = steps[0]?.direction === "drain";
  const where = band
    ? `through ${bandText(band)}, ${down ? "down and up" : "up and down"}`
    : down
      ? `down to ${formatCm(Math.min(...targets))} and back`
      : `up to ${formatCm(Math.max(...targets))} and back`;
  return `${n} ${where}${parts > 1 ? ` · part 1 of ${parts}` : ""}`;
}
export const targetStepText = (step) =>
  finiteNumber(step?.target_cm) === null ? "" : `Aim at ${formatCm(step.target_cm, 2)}`;

// How a target step came out: on target already (done without pumping), a
// hit (settled within the tolerance of its target), a miss (flagged, the run
// goes on), or not settled yet. null for a step with no target.
export function targetOutcome(step, tolerance = 0.06) {
  const target = finiteNumber(step?.target_cm);
  if (target === null) return null;
  const tol = finiteNumber(tolerance) ?? 0.06;
  const detail = step?.level_detail || {};
  const error = finiteNumber(step?.target_error_cm) ?? finiteNumber(detail.target_error_cm);
  const reached = finiteNumber(step?.level_cm);
  if (step?.level_source === "on_target" || detail.on_target === true)
    return {
      kind: "on_target",
      error,
      reached,
      text: "Already there: within the pump's resolution of its target, done without pumping",
    };
  if (error === null) return { kind: "pending", error: null, reached, text: "" };
  const hit = typeof detail.target_hit === "boolean" ? detail.target_hit : Math.abs(error) <= tol + 1e-9;
  return {
    kind: hit ? "hit" : "missed",
    error,
    reached,
    text: hit
      ? `${formatSignedCm(error, 2)}: a hit (within ±${trim(tol, 2)} cm)`
      : `${formatSignedCm(error, 2)}: missed (more than ±${trim(tol, 2)} cm; flagged, the run went on)`,
  };
}

// A fill commanded short of its target to keep the water under the tank's
// ceiling (level_detail.ceiling_clamped {commanded_cm, wanted_cm}).
export function ceilingClampText(step, ceiling = null) {
  const clamp = step?.level_detail?.ceiling_clamped;
  if (!clamp || typeof clamp !== "object") return "";
  const top = finiteNumber(ceiling);
  return `Commanded ${formatCm(clamp.commanded_cm)}, not ${formatCm(clamp.wanted_cm)}: kept under the tank ceiling${top !== null ? ` of ${formatCm(top, 2)}` : ""}, so it lands short of its target`;
}

// ---- Pacing: how long a plan takes ----

// The pump's seconds per commanded cm as the backend plans with it (the lab status's limits carry it), else the
// fallback. Never assume a number here: the Radar Lab must plan at the same rate as the server.
export const pumpSecondsPerCm = (limits) =>
  finiteNumber(limits?.pump_seconds_per_cm) ?? DEFAULT_PUMP_CONFIG.seconds_per_cm;

// The rate a lab run ran at: recorded with its calibration since 2026-10-19; older runs ran at 120 s per cm.
export const LEGACY_PUMP_SECONDS_PER_CM = 120;
export const runPumpSecondsPerCm = (run) =>
  finiteNumber(run?.calibration?.pump_seconds_per_cm) ?? LEGACY_PUMP_SECONDS_PER_CM;

// One step, start to start, as the server plans it (lab_rules.step_seconds()):
// its pump time, the engine's photos and settle (engine_overhead_s), then the
// settled data the level is read from (settled_dwell_min_s); at least the
// preset's own spacing_s and the floor step_spacing_min_s.
export function stepSeconds(step, preset, limits) {
  const cm = Math.abs(finiteNumber(step?.cm) ?? 0);
  const engine =
    cm * pumpSecondsPerCm(limits) +
    (finiteNumber(limits?.engine_overhead_s) ?? DEFAULT_LAB_ENGINE_OVERHEAD_SECONDS) +
    (finiteNumber(limits?.settled_dwell_min_s) ?? 60);
  return Math.max(engine, finiteNumber(preset?.spacing_s) ?? 0, finiteNumber(limits?.step_spacing_min_s) ?? 0);
}

// When each step starts, from the start of the lab run (after the baseline),
// and when the run ends (after the final dwell), in seconds.
export function planTimeline(preset, limits) {
  let at = finiteNumber(preset?.baseline_s) ?? 0;
  const starts = (preset?.steps || []).map((step) => {
    const start = at;
    at += stepSeconds(step, preset, limits);
    return start;
  });
  return { starts, end: at + (finiteNumber(preset?.final_dwell_s) ?? 0) };
}

const clockText = (seconds) => {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (h) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (m) return rest ? `${m} min ${rest} s` : `${m} min`;
  return `${rest} s`;
};
// "+1 h 05 min": a step's start from the start of the lab run.
export const offsetText = (seconds) => `+${clockText(seconds)}`;

// How a plan is paced, in words: as fast as the engine allows (spacing_s 0,
// every default), or a preset's own longer spacing.
export function pacingText(preset, limits) {
  const spacing = finiteNumber(preset?.spacing_s) ?? 0;
  const floor = finiteNumber(limits?.step_spacing_min_s) ?? 0;
  const overhead = finiteNumber(limits?.engine_overhead_s) ?? DEFAULT_LAB_ENGINE_OVERHEAD_SECONDS;
  const dwell = finiteNumber(limits?.settled_dwell_min_s) ?? 60;
  const oneCm = stepSeconds({ cm: 1 }, { spacing_s: 0 }, { ...limits, step_spacing_min_s: 0 });
  const fast = `as fast as the engine allows: its pump time, ${clockText(overhead)} for the engine's photos and settle, then ${clockText(dwell)} of settled data (${clockText(oneCm)} for a 1 cm step)`;
  const longer = Math.max(spacing, floor);
  return longer > 0 ? `each step ${fast}, at least ${clockText(longer)} apart (start to start)` : `each step ${fast}`;
}

export function groupByTier(presets) {
  const tiers = new Map();
  (presets || []).forEach((preset) => {
    const tier = finiteNumber(preset?.tier) ?? 0;
    if (!tiers.has(tier)) tiers.set(tier, []);
    tiers.get(tier).push(preset);
  });
  return [...tiers.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([tier, list]) => ({ tier, presets: list }));
}

// Why a preset cannot start now, in the order a person would fix them. With
// `rulerFit` (the plan checked from a ruler reading instead of the derived
// level), the level check and the server's own fit give way to it; `ruler`
// alone drops the level check (a ruler reading gives the level). `unplanned`:
// a plan the status did not preview (a target preset's part 2, another band,
// or from a ruler reading), which the server works out and checks when it
// starts, so the status's fit of its own plan does not apply.
export function presetBlockers(preset, status, { rulerFit = null, ruler = false, unplanned = false } = {}) {
  if (!preset) return [];
  const reasons = [];
  const fromRuler = ruler || !!rulerFit;
  const checks = (status?.checks || []).filter(
    (c) => !(fromRuler && c.key === "level_known"),
  );
  const failing = checks.filter((c) => !c.ok);
  const notReady = fromRuler ? failing.length > 0 : status?.ready === false;
  if (notReady)
    reasons.push(
      failing.length
        ? `Not ready: ${failing.map((c) => c.label || c.key).join("; ")}.`
        : "Not ready.",
    );
  if (preset.unlocked === false) {
    const tier = (status?.tiers || []).find((t) => t.tier === preset.tier);
    reasons.push(tier?.detail || `Tier ${preset.tier} is locked.`);
  }
  const fit = unplanned ? null : rulerFit || preset.fits;
  if (fit && fit.ok === false)
    reasons.push(fit.detail || "The plan does not fit the tank now.");
  if (preset.budget && preset.budget.ok === false)
    reasons.push(preset.budget.detail || "Over today's budget.");
  // The capture plan's requirements of the node (e.g. the phase plan's radar link of 38400 baud or faster).
  if (preset.node && preset.node.ok === false)
    reasons.push(preset.node.detail || "The node cannot run this preset's capture plan yet.");
  if (!reasons.length && !fromRuler && !unplanned && preset.can_start === false)
    reasons.push("The server does not allow this preset now.");
  return reasons;
}

// The step a lab run is on: the first one not finished, else the last.
export function currentStep(run) {
  const steps = run?.steps || [];
  if (!steps.length) return null;
  const index = steps.findIndex(
    (s) => !["done", "skipped", "failed", "cancelled"].includes(s.state),
  );
  const at = index === -1 ? steps.length - 1 : index;
  return { index: at, step: steps[at], total: steps.length };
}

export function countdownText(target, now = Date.now()) {
  const at = target ? Date.parse(target) : NaN;
  if (!Number.isFinite(at)) return "";
  const s = Math.round((at - now) / 1000);
  if (s <= 0) return "now";
  if (s < 60) return `in ${s} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (m < 60) return `in ${m} min${rest ? ` ${rest} s` : ""}`;
  return `in ${Math.floor(m / 60)} h ${m % 60} min`;
}

const ms = (value) => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isFinite(t) ? t : null;
};

// The chart's two lines: the level node 1 measured (level_cm per reading)
// and the level the plan expects: flat on each plateau, a straight ramp while
// the pump runs (it moves the level at a constant rate).
export function levelSeries(readings, run) {
  const measured = (readings || [])
    .map((p) => ({ ms: ms(p.t), level: finiteNumber(p.level_cm) }))
    .filter((p) => p.ms !== null && p.level !== null);
  const planned = [];
  const steps = run?.steps || [];
  const start =
    finiteNumber(run?.start_level_cm) ??
    finiteNumber(steps[0]?.planned_before_cm);
  const from =
    ms(run?.timeline?.baseline_from) ??
    ms(run?.timeline?.armed_at) ??
    ms(run?.armed_at) ??
    (measured[0]?.ms ?? null);
  if (start !== null && from !== null) {
    planned.push({ ms: from, level: start });
    let level = start;
    steps.forEach((step) => {
      const began = ms(step.pump_started_at);
      const ended = ms(step.pump_stopped_at);
      const after = finiteNumber(step.planned_after_cm);
      if (began === null || after === null) return;
      planned.push({ ms: began, level });
      level = after;
      planned.push({
        // Still pumping: the ramp ends when the pump's own timer does.
        ms: ended ?? began + pumpDurationSeconds(Math.abs(signedCm(step)), { seconds_per_cm: runPumpSecondsPerCm(run) }) * 1000,
        level,
      });
    });
    const to =
      ms(run?.timeline?.final_to) ??
      ms(run?.ended_at) ??
      (measured.length ? measured[measured.length - 1].ms : null);
    if (to !== null && to > planned[planned.length - 1].ms)
      planned.push({ ms: to, level });
  }
  return { measured, planned };
}

// The lab's own chart colours: the ruler camera's readings, the spectrum's
// estimate alone, and an estimate that jumped.
export const LAB_COLORS = { ruler: "#6b4fbb", spectrum: "#8aa0aa", jump: "#b42318" };

// The ruler camera's readings of a lab run's photos that count as ground truth
// (confident ones, with a time), for the level chart.
export function rulerPoints(run) {
  return (run?.ruler?.photos || [])
    .filter((p) => p.used && finiteNumber(p.level_cm) !== null)
    .map((p) => ({ ms: ms(p.t), level: finiteNumber(p.level_cm), step: p.step, which: p.which }))
    .filter((p) => p.ms !== null);
}

// The accuracy chart's error axis, ±mm: an error beyond it sits on the edge.
export const ACCURACY_LIMIT_MM = 30;
// Accuracy against the ruler camera (lab/validation/): the estimators compared,
// in order. `key` is the server's block for one (its fitted mapping and
// errors) and the prefix of its fields on every pair (<key>_error_cm). The
// server's `ours` is whichever estimator `estimator` names (v2 by default); it
// reports v1 and v2 by name too, and a server from before that only had `ours`
// for the one in use, so that row falls back to it (prefix `our`). A row with
// no block is left out. names: [while it gives ours, otherwise].
const ACCURACY_ESTIMATORS = [
  {
    key: "v2",
    names: ["Our estimate (v2)", "Estimator v2"],
    how: "the leading edge of the strongest echo",
  },
  { key: "v1", names: ["Our estimate (v1)", "Previous estimator (v1)"], how: "the peak of the Hann spectrum" },
  { key: "spectrum", names: ["Radar's own spectrum"], how: "a parabola on the radar's spectrum" },
  { key: "radar", names: ["Radar's own reading"], how: "its distance, bin-quantised" },
];
// Before estimator v2 a server sent no `estimator`: ours was v1's.
export const estimatorInUse = (data) => (data?.estimator === "v2" ? "v2" : "v1");

export function accuracyRows(data) {
  const inUse = estimatorInUse(data);
  return ACCURACY_ESTIMATORS.map(({ key, names, how }) => {
    const own = data?.[key] || null;
    const borrowed = !own && key === inUse ? data?.ours || null : null;
    return {
      key,
      name: names[key === inUse ? 0 : names.length - 1],
      how,
      inUse: key === inUse,
      fit: own || borrowed,
      prefix: borrowed ? "our" : key,
    };
  }).filter((row) => row.fit);
}

// cm from the server as mm to a tenth (signed: + or −, none at zero).
export function formatMm(cm, { signed = false } = {}) {
  const n = finiteNumber(cm);
  if (n === null) return "—";
  const mm = Math.round(n * 100) / 10;
  const sign = signed && mm > 0 ? "+" : signed && mm < 0 ? "−" : "";
  return `${sign}${Math.abs(mm).toFixed(1)} mm`;
}

// The share of an estimator's errors within 1 cm (1 - over_1cm / n), or null;
// shown to a tenth of a percent, so a single miss never reads as 100 %.
export function withinCmShare(errors) {
  const n = finiteNumber(errors?.n);
  const over = finiteNumber(errors?.over_1cm);
  return n && over !== null ? Math.min(1, Math.max(0, 1 - over / n)) : null;
}
export const shareText = (share) =>
  share === null ? "—" : share >= 1 ? "100 %" : `${Math.min(99.9, Math.round(share * 1000) / 10).toFixed(1)} %`;

// One estimator's errors for the accuracy chart: `level` the ruler's (cm), `mm`
// the error in mm held within ±limit; an error beyond it sits on the edge
// (beyond: 1 above, -1 below, 0 inside) and `error` keeps its true value.
export function accuracyPoints(pairs, prefix, limit = ACCURACY_LIMIT_MM) {
  const points = [];
  (pairs || []).forEach((pair) => {
    const level = finiteNumber(pair?.ruler_cm);
    const cm = finiteNumber(pair?.[`${prefix}_error_cm`]);
    if (level === null || cm === null) return;
    const error = Math.round(cm * 100) / 10;
    const beyond = error > limit ? 1 : error < -limit ? -1 : 0;
    points.push({ level, mm: beyond ? beyond * limit : error, error, beyond, t: pair.t });
  });
  return points;
}

// The level's three estimators (the server's lab_level), in words.
export const ESTIMATOR_NAMES = {
  beat_signal: "beat signal",
  spectrum: "spectrum",
  radar: "radar's own reading",
};
const estimatorName = (key) => ESTIMATOR_NAMES[key] || key;
const DOUBTS = {
  beat_spectrum_disagree: "beat signal and spectrum apart",
  clipping: "the beat signal clips",
  double_peak: "two near-equal beat-signal peaks",
  jumps: "an estimate jumped",
};
export const doubtText = (reasons) =>
  (reasons || []).map((r) => DOUBTS[r] || r.replace(/_/g, " ")).join(", ");

// What decided a settled level: a fresh ruler reading, the estimators that
// agreed (and the one left out), or the one estimator there was.
export function levelSourceText(record) {
  const source = record?.level_source;
  if (!source) return "";
  const detail = record?.level_detail || record || {};
  if (source === "ruler") {
    const ruler = detail.ruler || {};
    return ruler.kind === "entered" ? "Decided by the ruler reading entered for the run" : "Decided by the ruler camera";
  }
  if (source === "ruler_consistent") {
    return "Ruler camera, less confident, agreeing with the pump's expected level";
  }
  if (source === "pump_expected") {
    return "The pump's expected level (not confirmed: nothing could read the level, the run continued open loop)";
  }
  if (source === "on_target") {
    return "Already within the pump's resolution of its target: done without pumping";
  }
  if (source === "estimators") {
    const agreed = (detail.agreed || []).map(estimatorName);
    const parts = [`Estimators agreed${agreed.length ? `: ${agreed.join(", ")}` : ""}`];
    if (detail.dropped) parts.push(`${estimatorName(detail.dropped)} left out`);
    return parts.join("; ");
  }
  return `${estimatorName(source).replace(/^./, (c) => c.toUpperCase())} alone`;
}

// A lab run's flags (the server raises them; none stops a run), in words.
// `limits`: the run's own, for the numbers.
const FLAGS = {
  mirrored: () => "Mirrored: it drained first, as the preset as written would not fit from the start level",
  ruler_reading: () => "A ruler reading entered before the first step set the start level",
  ceiling_clamped: (l) =>
    `A fill was commanded short of its target to keep the water under the tank ceiling${finiteNumber(l?.tank_ceiling_cm) !== null ? ` of ${formatCm(l.tank_ceiling_cm, 2)}` : ""}`,
  target_missed: (l) =>
    `A step settled more than ±${trim(finiteNumber(l?.target_tolerance_cm) ?? 0.06, 2)} cm from its target: flagged, the run went on`,
  ruler_decided: () => "The ruler camera decided a settled level",
  ruler_consistent: () => "A less confident ruler reading, agreeing with the pump's expected level, confirmed a step",
  pump_expected: () => "A step continued open loop on the pump's expected level (nothing could read the level)",
  estimators_disagreed: () => "The node's estimators disagreed on a settled level (it held for them or a ruler reading)",
  estimator_jumps: () => "An estimate jumped faster than the pump moves the water",
  low_confidence: () => "A settled level came from a doubtful window",
  radar_fallback: () => "A settled level rests on the radar's own reading alone (no beat signal or spectrum)",
  radar_flip: () => "The radar's own reading flipped a whole bin",
  calibration_bounded: () => "A refined pump factor reached its bounds",
  calibration_inconsistent: () => "A direction's step ratios did not agree, so its pump factor was not refined",
  step_left_in_flight: () => "A pump step was still under way when the run ended",
  restore_unconfirmed: () => "The node did not confirm that its settings were restored",
  captures_missing: () => "The node stored no radar capture during the run",
};
export const flagText = (flag, limits = null) =>
  FLAGS[flag] ? FLAGS[flag](limits) : String(flag || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// What a stop reason asks of a person, beyond the server's own words.
const REASON_ADVICE = {
  over_ceiling:
    "A fill already pumping finishes on the controller's own timer: check the tank against its ruler and drain by hand if needed.",
};
export const reasonAdvice = (reason) => REASON_ADVICE[String(reason || "").split(":")[0]] || "";
// The server's words for why a lab run ended (reason_text), else ours.
const REASONS = {
  over_ceiling:
    "The water reached the tank ceiling by the ruler, or a fill could have taken it over (the pump at its factor plus its spread).",
};
export const reasonText = (record) =>
  record?.reason_text || REASONS[String(record?.reason || "").split(":")[0]] || String(record?.reason || "").replace(/_/g, " ");

// A step's ruler readings: before → after and the change, when both photos
// were read with confidence; else what is missing.
export function rulerStepText(step, photos = {}, min = 0.7) {
  const before = finiteNumber(step?.ruler_before_cm);
  const after = finiteNumber(step?.ruler_after_cm);
  const change = finiteNumber(step?.ruler_change_cm);
  const state = (id, level, confidence) => {
    if (!id) return "no photo";
    if (level !== null) return null;
    if (finiteNumber(confidence) === null) return "not read yet";
    return photos[id]?.reason ? `refused: ${photos[id].reason}` : "refused";
  };
  const missing = [
    ["before", state(step?.photo_before_id, before, step?.ruler_before_confidence)],
    ["after", state(step?.photo_after_id, after, step?.ruler_after_confidence)],
  ].filter(([, text]) => text);
  if (before === null && after === null && !step?.photo_before_id && !step?.photo_after_id) return { text: "—", detail: "" };
  const text = `${before !== null ? formatCm(before, 2) : "?"} → ${after !== null ? formatCm(after, 2) : "?"}`;
  const details = [];
  if (change !== null) details.push(`${formatSignedCm(change, 2)} by the ruler`);
  missing.forEach(([which, what]) => details.push(`${which}: ${what}`));
  const low = [step?.ruler_before_confidence, step?.ruler_after_confidence]
    .map(finiteNumber)
    .filter((c) => c !== null && c > 0);
  const floor = finiteNumber(min) ?? 0.7;
  if (low.length && Math.min(...low) < floor) details.push(`confidence ${Math.min(...low).toFixed(2)}`);
  return { text, detail: details.join(" · ") };
}

export const captureKinds = (capture) => {
  const parts = [];
  if (Array.isArray(capture?.db) && capture.db.some((v) => v !== null))
    parts.push("spectrum");
  if (capture?.has_adc) parts.push("beat signal");
  return parts.length ? parts.join(" + ") : "no spectrum or beat signal";
};

export function loadSelection() {
  try {
    const value = JSON.parse(localStorage.getItem(SELECTION_KEY) || "{}");
    return {
      node: typeof value?.node === "string" ? value.node : "",
      controller: typeof value?.controller === "string" ? value.controller : "",
    };
  } catch {
    return { node: "", controller: "" };
  }
}
export function saveSelection(selection) {
  try {
    localStorage.setItem(
      SELECTION_KEY,
      JSON.stringify({
        node: selection?.node || "",
        controller: selection?.controller || "",
      }),
    );
  } catch {}
}

// What a start does, for the confirmation: the node settings sent, how often
// the pump runs, and what is left alone.
// A target preset's steps come from the server's status (part 1, its own
// band, from the derived level); for part 2, another band or a ruler reading
// (`planned` false) the server works them out when it starts.
export function startSummary(preset, capturePlan, node, { limits = null, part = 1, band = null, planned = true } = {}) {
  const steps = preset?.steps || [];
  const settings = capturePlan?.settings || {};
  const live = capturePlan?.live || {};
  const settingText = Object.entries(settings)
    .map(([k, v]) => `${k} ${v}`)
    .join(", ");
  const liveText = live.live_s
    ? `live mode with a reading every ${live.live_s} s${live.keep ? ", kept on while on external power" : ""}`
    : "";
  const parts = finiteNumber(preset?.parts) ?? 1;
  const which = [parts > 1 ? `part ${part} of ${parts}` : "", bandOf({ band }) ? `through ${bandText(band)}` : ""]
    .filter(Boolean)
    .join(", ");
  const pumpLine = !isTargetPlan(preset)
    ? `The pump runs ${steps.length} time${steps.length === 1 ? "" : "s"} (${planText(steps)}), ${minutesText(preset?.pump_s)} of pumping in all, one step at a time with a photo before and after each.`
    : planned && steps.length
      ? `The pump runs ${steps.length} time${steps.length === 1 ? "" : "s"} (${targetPlanText(preset)}), about ${minutesText(preset?.pump_s)} of pumping in all, one step at a time with a photo before and after each. Each step aims at a level on the ruler and is commanded when it is due, from the level the step before it settled at.`
      : `The server works the steps out from the level when it starts${which ? ` (${which})` : ""}, and refuses them if they do not fit the tank. Each step aims at a level on the ruler and is commanded when it is due, from the level the step before it settled at; one step at a time with a photo before and after each.`;
  const ceiling = finiteNumber(limits?.tank_ceiling_cm);
  const deepest = finiteNumber(limits?.max_drain_cm);
  const guards = [
    ceiling !== null
      ? `No fill that could pass the tank ceiling (${formatCm(ceiling, 2)} on the ruler) is pumped, and a ruler reading above it stops the lab run at once`
      : "",
    deepest !== null ? `the water goes at most ${formatCm(deepest)} below its start` : "",
  ].filter(Boolean);
  return [
    `${node?.name || node?.mac || "The node"} gets ${[settingText, liveText].filter(Boolean).join(" and ") || "its capture plan"}; the settings the lab run changed are restored afterwards (only those still holding its values).`,
    pumpLine,
    ...(guards.length ? [`${guards.join("; ").replace(/^./, (c) => c.toUpperCase())}.`] : []),
    "The greenhouse temperature and humidity are not touched: no Experiment run is started.",
    "Do not push to padihat-backend dev while the lab run is going: a deploy restarts the services that run the pump steps.",
  ];
}
